import { GenericAlert } from "@studio3t/generic-components";
import { useId } from "react";
import { PemField } from "./PemField";
import { SelectDropdown, type SelectOption } from "./SelectDropdown";
import {
  FieldPairAside,
  FieldPairRow,
  FieldRow,
  FieldRowControl,
  FormGroup,
  FormSection,
  HelpText,
  Input,
  Label,
  SectionTitle,
} from "./styles";

export type AuthMode = "none" | "x509_inline";

type WriteMode = "insert" | "replace";

const WRITE_MODE_OPTIONS: SelectOption<WriteMode>[] = [
  { value: "insert", label: "Insert (default)" },
  { value: "replace", label: "Replace (upsert by _id)" },
];

const AUTH_MODE_OPTIONS: SelectOption<AuthMode>[] = [
  { value: "none", label: "None (connection string)" },
  { value: "x509_inline", label: "x509 Certificates" },
];

interface MongoDBFieldsProps {
  connectionString: string;
  setConnectionString: (value: string) => void;
  dbName: string;
  setDbName: (value: string) => void;
  writeMode: WriteMode;
  setWriteMode: (value: WriteMode) => void;
  authMode: AuthMode;
  setAuthMode: (value: AuthMode) => void;
  clientCert: string;
  setClientCert: (value: string) => void;
  clientKey: string;
  setClientKey: (value: string) => void;
  caCert: string;
  setCaCert: (value: string) => void;
}

function X509CertFields({
  clientCert,
  setClientCert,
  clientKey,
  setClientKey,
  caCert,
  setCaCert,
}: Pick<
  MongoDBFieldsProps,
  | "clientCert"
  | "setClientCert"
  | "clientKey"
  | "setClientKey"
  | "caCert"
  | "setCaCert"
>) {
  const clientCertId = useId();
  const clientKeyId = useId();
  const caCertId = useId();

  return (
    <>
      <PemField
        label="Client Certificate (PEM) *"
        id={clientCertId}
        value={clientCert}
        onChange={setClientCert}
        accept=".pem,.crt,.cer"
        ariaLabel="Upload client certificate PEM file"
        placeholder={
          "-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----"
        }
        helpText="Upload a .pem file or paste the client certificate content"
        required
      />
      <PemField
        label="Client Key (PEM) *"
        id={clientKeyId}
        value={clientKey}
        onChange={setClientKey}
        accept=".pem,.key"
        ariaLabel="Upload client key PEM file"
        placeholder={
          "-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----"
        }
        helpText="Upload a .pem file or paste the client private key content"
        required
      />
      <PemField
        label="CA Certificate (PEM)"
        id={caCertId}
        value={caCert}
        onChange={setCaCert}
        accept=".pem,.crt,.cer"
        ariaLabel="Upload CA certificate PEM file"
        placeholder={
          "-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----"
        }
        helpText="Optional — leave empty to use the system trusted CA bundle"
      />
    </>
  );
}

export function MongoDBFields({
  connectionString,
  setConnectionString,
  dbName,
  setDbName,
  writeMode,
  setWriteMode,
  authMode,
  setAuthMode,
  clientCert,
  setClientCert,
  clientKey,
  setClientKey,
  caCert,
  setCaCert,
}: MongoDBFieldsProps) {
  const connectionStringId = useId();
  const dbNameId = useId();

  return (
    <>
      <FormSection>
        <SectionTitle>MongoDB configuration</SectionTitle>

        <FormGroup>
          <Label htmlFor={connectionStringId}>Connection string*</Label>
          <Input
            id={connectionStringId}
            type="text"
            value={connectionString}
            onChange={(e) => setConnectionString(e.target.value)}
            placeholder="mongodb://localhost:27017 or mongodb+srv://..."
            required
          />
          <HelpText>
            Use mongodb:// for local or mongodb+srv:// for Atlas
          </HelpText>
        </FormGroup>

        {/* Database name + Write mode share one row per Figma 14055-95222. */}
        <FieldPairRow>
          <FormGroup>
            <Label htmlFor={dbNameId}>Database name*</Label>
            <Input
              id={dbNameId}
              type="text"
              value={dbName}
              onChange={(e) => setDbName(e.target.value)}
              placeholder="Add a name to the database"
              required
            />
          </FormGroup>

          <FieldPairAside>
            <Label as="span">Write mode</Label>
            <SelectDropdown
              value={writeMode}
              options={WRITE_MODE_OPTIONS}
              onChange={setWriteMode}
            />
          </FieldPairAside>
        </FieldPairRow>
      </FormSection>

      <FormSection>
        <SectionTitle>Authentication</SectionTitle>

        <FieldRow>
          <Label as="span">Auth mode</Label>
          <FieldRowControl>
            <SelectDropdown
              value={authMode}
              options={AUTH_MODE_OPTIONS}
              onChange={setAuthMode}
            />
            <HelpText>
              Select &quot;None&quot; to authenticate via the connection string
              URI (SCRAM-SHA-1, SCRAM-SHA-256 or PLAIN). Use x509 for mutual TLS
              with PEM certificates.
            </HelpText>
          </FieldRowControl>
        </FieldRow>

        {authMode === "x509_inline" && (
          <GenericAlert
            state="Info"
            icon
            dismissible={false}
            width="100%"
            message="When using x509 certificates, only the host URI is needed, TLS is configured automatically."
          />
        )}

        {authMode === "x509_inline" && (
          <X509CertFields
            clientCert={clientCert}
            setClientCert={setClientCert}
            clientKey={clientKey}
            setClientKey={setClientKey}
            caCert={caCert}
            setCaCert={setCaCert}
          />
        )}
      </FormSection>
    </>
  );
}
